import React, {useEffect, useState} from 'react';
import {commentCase, fetchCasesById, fetchCaseChildComments} from '../../features/thunks';
import {selectCaseById} from '../../features/casesSlice'
import {useDispatch, useSelector} from 'react-redux';
import SeoTags from '../SeoTags';
import ContentSection from '../ContentSection';
import {createVideoUrl, getVideoThumbnailPath} from '../../util/video';
import VideoCard from '../cards/VideoCard';
import {formatDate, formatName} from '../../util/format';
import {CaseAddReactionForm} from '../forms/AddReactionForm';
import {useAuth} from '../../util/auth';
import {CaseStatuses, DEFAULT_SESSION_DURATION_IN_MINUTES} from '../../constants';
import CaseParticipantsModal from './CaseParticipantsModal';
import GiveFeedbackCta from '../GiveFeedbackCta';
import {selectTimezone} from '../../features/timezoneSlice';
import RegisterButton from '../RegisterButton';
import {useAddToCalendar} from '../AddCaseToCalendarLink';
import SigninConditionalRenderer from '../SigninConditionalRenderer';
import {useTrainingPlan} from '../../util/hooks/useTrainingPlan';
import {trainingPlanResourceComplete} from '../../util/trainingplan';
import {selectCommentEntities} from '../../features/commentsSlice';
import {Badge} from '../ui/badge';
import {UserAvatar} from '../UserAvatar';
import {CommentsDrawer} from '@/components/Case/CommentsDrawer';
import {Typography} from '@/components/ui/typography';
import Breadcrumbs from '@/components/Breadcrumbs';
import {selectUserById} from '@/features/usersSlice';
import {Bookmark, CalendarClock, Clock4, Heart, MessageCircle, Users} from 'lucide-react';
import {Button} from '@/components/ui/button';
import {Separator} from '@/components/ui/separator';
import {useAddCaseBookmarks, useAddCaseToFavorites} from '@/util/hooks/useBookmarks';
import {CommentsSection} from '@/components/Toolbox/CommentsSection';
import {User} from '@/types/user';
import {uniqBy} from "lodash";


const caseStateMap = {
    [CaseStatuses.TENTATIVE]: 'Schedule',
    [CaseStatuses.SCHEDULED]: 'Schedule',
    [CaseStatuses.NEW]: 'New cases',
    [CaseStatuses.CLOSED_PRIVATE]: 'Closed',
    [CaseStatuses.CLOSED]: 'Case challenge',
};

export type CasePageProps = {
    id: string;
}

const CasePage = ({id: caseId}: CasePageProps) => {
    const dispatch = useDispatch();
    const currentCase = useSelector((state) => selectCaseById(state, caseId));
    const [isCommentsDrawerOpen, setIsCommentsDrawerOpen] = useState(false);
    const [isParticipantsModalOpen, setIsParticipantsModalOpen] = useState(false);

    const {plan: trainingPlan, getIsResourcePartOfTheTrainingPlan, getIsResourceCompleted, getIsResourceReviewed, reloadTrainingPlanById, status} = useTrainingPlan();
    const isResourcePartOfTheTrainingPlan = getIsResourcePartOfTheTrainingPlan(caseId);
    const isResourceCompleted = getIsResourceCompleted(caseId);
    const isResourceReviewed = getIsResourceReviewed(caseId);
    const isCaseChallengeSolved = useIsCaseChallengeSolved(currentCase, {
        isTrainingPlanMaterial: isResourcePartOfTheTrainingPlan,
        isCompleted: isResourceCompleted,
        isReviewed: isResourceReviewed,
    });

    useEffect(() => {
        if (caseId) {
            dispatch(fetchCasesById({caseId}));
        }
    }, [dispatch, caseId]);

    if (!caseId || !currentCase) {
        return null;
    }

    return (
        <>
            <SeoTags config={{
                title: `Casehub - ${currentCase.title}`
            }} />
            <div className="container">
                <div className="w-full max-w-screen lg:w-1/2 m-auto pt-8 pb-16">
                    <Header
                        currentCase={currentCase}
                        setIsCommentsDrawerOpen={setIsCommentsDrawerOpen}
                        setIsParticipantsModalOpen={setIsParticipantsModalOpen}
                        isTrainingPlanMaterial={isResourcePartOfTheTrainingPlan}
                    />
                    <Content
                        trainingPlan={trainingPlan}
                        reloadTrainingPlanById={reloadTrainingPlanById}
                        isTrainingPlanLoading={status === 'loading'}
                        currentCase={currentCase}
                        openCommentsDrawer={() => setIsCommentsDrawerOpen(true)}
                        isParticipantsModalOpen={isParticipantsModalOpen}
                        setIsParticipantsModalOpen={setIsParticipantsModalOpen}
                        isTrainingPlanMaterial={isResourcePartOfTheTrainingPlan}
                        isCompleted={isResourceCompleted}
                        isReviewed={isResourceReviewed}
                    />
                </div>
            </div>
            <CommentsDrawer
                caseId={caseId}
                open={isCommentsDrawerOpen}
                onOpenChange={setIsCommentsDrawerOpen}
                trainingPlan={trainingPlan || undefined}
                blurComments={!isCaseChallengeSolved}
                reloadTrainingPlanById={reloadTrainingPlanById}
            />
            <CaseParticipantsModal
                caseId={caseId}
                open={isParticipantsModalOpen}
                onOpenChange={setIsParticipantsModalOpen}
            />
        </>
    );
};

const Header = ({
    currentCase,
    setIsCommentsDrawerOpen,
    setIsParticipantsModalOpen,
    isTrainingPlanMaterial
}: {
    currentCase: Case;
    setIsCommentsDrawerOpen: (isOpen: boolean) => void;
    setIsParticipantsModalOpen: (isOpen: boolean) => void;
    isTrainingPlanMaterial: boolean;
}) => {
    const timezone = useSelector(selectTimezone);
    const {user: userId} = currentCase;
    const user = useSelector(state => userId ? selectUserById(state, userId) : null);
    const isScheduledCase = [CaseStatuses.TENTATIVE, CaseStatuses.SCHEDULED].includes(currentCase.state);
    const isUpcoming = currentCase.scheduled_to && currentCase.scheduled_to > (Date.now() / 1000)

    const addToBookmarks = useAddCaseBookmarks(currentCase.id);
    const addToFavorites = useAddCaseToFavorites(currentCase.id);

    return <div>
        <Breadcrumbs list={[
            {id: 'group_sessions', name: 'Group sessions'},
            {id: currentCase.id, name: caseStateMap[currentCase.state] || currentCase.state},
            {id: currentCase.title, name: currentCase.title},
        ]} />
        <div className="flex flex-col gap-2 mb-4">
            <div className="flex gap-2 mt-4 mb-2">
                {isTrainingPlanMaterial ? (
                    <Badge variant="secondary" className="bg-teal-200">
                        Part of my training plan
                    </Badge>
                ) : null}
            </div>
            <Typography variant="h2" asChild className="pb-0">
                <h1>{currentCase.title}</h1>
            </Typography>
            <div className="flex gap-1 text-muted-foreground text-xs flex-wrap">
                {uniqBy((currentCase.expertises || []), 'id').map((exp) => (
                    <span>#{exp.title}</span>
                ))}
            </div>
        </div>
        <div className="flex items-center space-x-2 mb-6">
            <UserAvatar userId={user?.id} className="h-10 w-10"/>
            {user && (
                <div>
                    <p className="text-xs font-medium leading-none">
                        {formatName(user)}
                    </p>
                    <p className="text-xs text-muted-foreground">{user.position}</p>
                </div>
            )}
        </div>
        <div className="flex flex-wrap gap-2">
            <Button variant="plain" size="subtle" onClick={() => {
                setIsParticipantsModalOpen(true)
            }}>
                <Users className="w-4 h-4" />
                {currentCase.register_count}/{currentCase.register_max}
            </Button>
            {
                !isUpcoming && (
                    <Button variant="plain" size="subtle" onClick={() => {
                        setIsCommentsDrawerOpen(true)
                    }}>
                        <MessageCircle className="w-4 h-4" />
                        {currentCase.comment_count}
                    </Button>
                )
            }
            {isScheduledCase && (
                <>
                    <div className="flex gap-4">
                        <div className="flex gap-x-2 text-sm items-center">
                            <CalendarClock className="w-4 h-4"/> {formatDate({
                            ts: currentCase.scheduled_to,
                            format: 'long',
                            tz: timezone
                        })}
                        </div>
                        <div className="flex gap-x-2 text-sm items-center">
                            <Clock4
                                className="w-4 h-4"/> {currentCase.duration || DEFAULT_SESSION_DURATION_IN_MINUTES} min
                        </div>
                    </div>
                </>
            )}
            {currentCase.id ? (<div className="ml-auto">
                <Button variant="ghost" size="subtle" onClick={addToFavorites}>
                    <Heart className="w-4 h-4" style={{
                        fill: currentCase.user_favorite ? '#000' : 'none'
                    }}/>
                </Button>
                <Button variant="ghost" size="subtle" onClick={addToBookmarks}>
                    <Bookmark className="w-4 h-4" style={{
                        fill: currentCase.user_bookmark ? '#000' : 'none'
                    }}/>
                </Button>
            </div>) : null}
        </div>
        {isScheduledCase ? (
            <div className="flex justify-between my-4">
                <RegisterButton caseId={currentCase.id} />
            </div>
        ) : null}
        <Separator className="mt-4"/>
    </div>
}

const useIsCaseChallengeSolved = (
    currentCase,
    {
        isTrainingPlanMaterial = false,
        isCompleted = false,
        isReviewed = false,
    }: {
        isTrainingPlanMaterial?: boolean;
        isCompleted?: boolean;
        isReviewed?: boolean;
    } = {}
) => {
    const auth = useAuth();
    const userId = auth?.user?.id;

    const comments = useSelector(state => {
        const entries = selectCommentEntities(state);
        return (currentCase?.comments || []).map(id => entries[id]);
    });

    if (!userId || !currentCase) {
        return false;
    }

    if (currentCase?.user === userId) {
        return true;
    }

    if ([
        'ed83cad71b053a12bb423e2825e11221',
        '66eca213dad89464562f5c6fd4641e49',
        '684fa4d54380170fe790632a0643990f',
    ].includes(userId)) {
        return true;
    }

    if (![CaseStatuses.CLOSED].includes(currentCase?.state)) {
        return true;
    }

    if (isTrainingPlanMaterial) {
        return !!(isCompleted || isReviewed);
    }

    return comments.some(it => it.user === userId);
}

type Case = {
    user_favorite?: boolean;
    user_bookmark?: boolean;
    expertises: {
        id: string;
        title: string;
    }[];
    duration: number;
    id: string;
    state: CaseStatuses;
    user_participant: boolean
    user_trainingplan: boolean,
    scheduled_to?: number;
    comment_count: number;
    videos: {
        id: string;
        title: string;
        content: string;
    }[];
    content: string;
    key_question: string;
    title: string;
    subtitle: string;
    public: boolean;
    summary: string;
    register_max: number;
    register_count: number;
    user: string;
}

type TrainingPlan = {
    id: string;
    resources: {
        resource_id: string;
        id: string;
        completed: boolean;
    }[]
}

type ContentProps = {
    currentCase: Case;
    openCommentsDrawer: () => void;
    isParticipantsModalOpen: boolean;
    setIsParticipantsModalOpen: (value: boolean) => void;
    isTrainingPlanMaterial: boolean;
    isCompleted: boolean;
    isReviewed: boolean;
    trainingPlan: TrainingPlan;
    reloadTrainingPlanById: (id: string) => void;
}
const Content = ({currentCase, openCommentsDrawer, setIsParticipantsModalOpen, isTrainingPlanMaterial, isTrainingPlanLoading, isCompleted, isReviewed, trainingPlan, reloadTrainingPlanById}: ContentProps) => {
    const isCaseChallengeSolved = useIsCaseChallengeSolved(currentCase, {
        isTrainingPlanMaterial,
        isCompleted,
        isReviewed,
    });

    if (!currentCase) {
        return null;
    }

    const isClosedCase = [CaseStatuses.CLOSED].includes(currentCase.state);
    const allCaseVideos = (currentCase.videos || []);
    const caseVideosToShow = allCaseVideos.slice(0, !isCaseChallengeSolved ? 1 : allCaseVideos.length);

    return (
        <div className="grid grid-cols-12 lg:space-x-12">
            <div className="col-span-12 my-4">
                {
                    isClosedCase ? <>
                        <ContentSection
                            title={"Instruction"}
                            content={"Read the case description and the key question. Watch the below recording of the Q&A session with the case owner.\n" +
                                "Think of possible solutions and write your recommendations to the 'share your ideas' field below.\n" +
                                "After you have done this you can see what other club members have offered and what solution was reached at the actual Casehub session."}
                        />
                    </> : null
                }

                <ContentSection
                    title={"Case description"}
                    content={currentCase.content}
                />
                {
                    (currentCase.key_question) ? (
                        <ContentSection
                            title={"Key question"}
                            content={currentCase.key_question}
                        />
                    ) : null
                }
                {
                    (currentCase.videos && currentCase.videos.length) ? (
                        <div>
                            {caseVideosToShow.length > 1 ? (
                                <Typography variant="h4" className="mb-6">
                                    Sessions clips
                                </Typography>
                            ) : null}
                            {caseVideosToShow.map((v) => {
                                const url = createVideoUrl(v);
                                const thumb = getVideoThumbnailPath(v);
                                return (
                                    <div className="py-4" key={v.id}>
                                        <VideoCard
                                            title={v.title}
                                            description={v.content}
                                            embedUrl={url}
                                            thumb={thumb}
                                            authRequired={!currentCase.public}
                                        />
                                    </div>
                                )
                            })}
                        </div>
                    ) : null
                }
                {isCaseChallengeSolved && currentCase.summary ? (
                    <ContentSection
                        title={"Case summary"}
                        htmlContent
                        content={currentCase.summary}
                    />
                ) : null}

                {(() => {
                    if (!isClosedCase) {
                        return (
                            <CommentsDataProvider
                                showCommentsList={!isTrainingPlanLoading}
                                caseId={currentCase.id}
                                afterCommentSubmitted={() => {
                                    reloadTrainingPlanById(trainingPlan?.id);
                                }}
                            />
                        )
                    }

                    if (isTrainingPlanMaterial && !isCompleted) {
                        return (
                            <GiveFeedbackCta
                                title={'Your ideas'}
                                subtitle={'What solution would you recommend to the case owner? Which CaseHub tools would you suggest the owner use, if any? After submitting your suggestions, you\'ll see what other Casehub members have offered and what the live session outcome was.'}
                                ctaText={''}
                                ctaBefore={<>
                                    <SigninConditionalRenderer
                                        title="You must be logged in to add comments"
                                        buttonText="Log in to continue"
                                    >
                                        <CaseAddReactionForm
                                            caseId={currentCase.id}
                                            placeholder={'How would you solve the case?'}
                                            ctaText={'Submit'}
                                            onSubmitCommentButtonClick={() => {
                                                openCommentsDrawer();
                                            }}
                                            afterCommentSubmitted={() => {
                                                reloadTrainingPlanById(trainingPlan?.id);
                                            }}
                                        />
                                    </SigninConditionalRenderer>
                                </>}
                            />
                        )
                    }

                    if (isTrainingPlanMaterial && (isCompleted && !isReviewed)) {
                        return (
                            <GiveFeedbackCta
                                title={'Final reflections'}
                                subtitle={'After watching the videos and summary, what are your key takeaways? How will you use these learnings in your work going forward? Submit your final reflections to complete this Case Challenge.'}
                                ctaText={''}
                                ctaBefore={<>
                                    <SigninConditionalRenderer
                                        title="You must be logged in to add comments"
                                        buttonText="Log in to continue"
                                    >
                                        <CaseAddReactionForm
                                            caseId={currentCase.id}
                                            placeholder={'Share your final reflections'}
                                            ctaText={'Submit'}
                                            onSubmitCommentButtonClick={() => {
                                                openCommentsDrawer();
                                            }}
                                            afterCommentSubmitted={() => {
                                                reloadTrainingPlanById(trainingPlan?.id);
                                            }}
                                        />
                                    </SigninConditionalRenderer>
                                </>}
                            />
                        )
                    }

                    if (!isCaseChallengeSolved) {
                        return (
                            <GiveFeedbackCta
                                title={'How would you solve this case? '}
                                ctaText={''}
                                subtitle={'Which CaseHub tool would you use, and how? After writing your suggestions, you\'ll see what other Casehub members have offered and what was the live session outcome.'}
                                ctaBefore={<>
                                    <SigninConditionalRenderer
                                        title="You must be logged in to add comments"
                                        buttonText="Log in to continue"
                                    >
                                        <CaseAddReactionForm
                                            placeholder={'How would you solve the case?'}
                                            caseId={currentCase.id}
                                            ctaText={'Share your ideas'}
                                            onSubmitCommentButtonClick={() => {
                                                openCommentsDrawer();
                                            }}
                                            afterCommentSubmitted={() => {
                                                reloadTrainingPlanById(trainingPlan?.id);
                                            }}
                                        />
                                    </SigninConditionalRenderer>
                                </>}
                            />
                        )
                    }

                    if (!isTrainingPlanMaterial) {
                        return (
                            <CommentsDataProvider showCommentsList={!isTrainingPlanLoading} caseId={currentCase.id} />
                        )
                    }

                    return (
                        <>
                            <CommentsDataProvider showCommentsList={!isTrainingPlanLoading} caseId={currentCase.id} onSubmitCommentButtonClick={() => {
                                openCommentsDrawer();
                            }} afterCommentSubmitted={() => {
                                reloadTrainingPlanById(trainingPlan?.id);
                            }}/>
                            {
                                false && (
                                    <GiveFeedbackCta
                                        ctaText={'Write your comment'}
                                        onClick={() => {
                                            openCommentsDrawer();
                                        }}
                                    />
                                )
                            }
                        </>
                    )
                })()}
            </div>
        </div>
    )
}

const CommentsDataProvider = ({caseId, onSubmitCommentButtonClick, onSubmitReplyButtonClick, afterCommentSubmitted, showCommentsList}: {
    caseId: string;
    onSubmitCommentButtonClick?: (comment: string) => void;
    onSubmitReplyButtonClick?: (parentId: string, comment: string) => void;
    afterCommentSubmitted?: () => void;
    showCommentsList?: boolean;
}) => {
    const currentCase = useSelector(state => selectCaseById(state, caseId));
    const comments = currentCase?.comments || [];
    const dispatch = useDispatch();
    const auth = useAuth();
    const user = auth?.user;

    if (!currentCase) {
        return null;
    }

    if (!user) {
        return null;
    }

    return <CommentsSection
        comments={comments}
        user={auth.user as User}
        showCommentsList={showCommentsList}
        onSubmitCommentButtonClick={(text) => {
            if (onSubmitCommentButtonClick) {
                onSubmitCommentButtonClick(text);
            }
            dispatch(commentCase({caseId, comment: text, userId: user.id})).then(() => {
                if (afterCommentSubmitted) {
                    afterCommentSubmitted();
                }
            });
        }}
        onSubmitReplyButtonClick={(parentId, text) => {
            if (onSubmitReplyButtonClick) {
                onSubmitReplyButtonClick(parentId, text);
            }
            dispatch(commentCase({caseId, comment: text, userId: user.id, parentId: parentId})).then(() => {
                if (afterCommentSubmitted) {
                    afterCommentSubmitted();
                }
            })
        }}
        onFetchReply={(commentId) => {
            dispatch(fetchCaseChildComments({caseId, commentId: commentId}));
        }}
    />
}

export default CasePage;
